import React, { useState } from 'react';
import axios from 'axios';
import { createApiUrlWithPrefix } from '../config/api';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CreditCard, X } from 'lucide-react';

const PersonnelAddPaymentModal = ({ personnel, isOpen, onClose, refreshList }) => {
  const { t } = useTranslation();
  const [paymentData, setPaymentData] = useState({
    description: `Salary for ${personnel?.firstName || personnel?.name || ''}`,
    amount: '',
    type: 'Salary',
    paymentDate: new Date().toISOString().split('T')[0]
  });

  const paymentTypes = [
    { value: 'Salary', label: t('common.salary') },
    { value: 'Salary Advance', label: t('common.salaryAdvance') },
    { value: 'Bonus', label: t('common.bonus') },
    { value: 'Overtime', label: t('common.overtime') },
    { value: 'Allowance', label: t('common.allowance') },
    { value: 'Reimbursement', label: t('common.reimbursement') },
    { value: 'Other', label: t('common.other') }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        personnelId: personnel.id,
        paymentType: paymentData.type,
        description: paymentData.description,
        amount: parseFloat(paymentData.amount),
        paymentDate: new Date(paymentData.paymentDate).toISOString(),
        notes: '',
        paymentMethod: 'Bank Transfer',
        referenceNumber: ''
      };
      await axios.post(createApiUrlWithPrefix('PersonnelPayment'), payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success(t('common.paymentCreatedSuccessfully'));
      onClose();
      if (refreshList) refreshList();
    } catch (error) {
      console.error('Error saving payment:', error);
      toast.error(t('common.failedToSavePayment'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3 flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t('common.addPayment')} for {personnel?.firstName || personnel?.name}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('common.paymentTypeLabel')}</label>
            <select
              value={paymentData.type}
              onChange={(e) => setPaymentData({ ...paymentData, type: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            >
              {paymentTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('common.descriptionLabel')}</label>
            <input
              type="text"
              value={paymentData.description}
              onChange={(e) => setPaymentData({ ...paymentData, description: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('common.descriptionPlaceholder')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('common.amountLabel')}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={paymentData.amount}
              onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('common.amountPlaceholder')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('common.paymentDateLabel')}</label>
            <input
              type="date"
              value={paymentData.paymentDate}
              onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              {t('common.cancelLabel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              {t('common.createPayment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonnelAddPaymentModal;
